/**
 * Prüfungsreife-Score — Client-side exam readiness calculator
 *
 * 5 weighted components → 0-100 score:
 *   30% Simulation score (last 3 exams)
 *   25% Chapter coverage (avg chapter progress)
 *   20% Hard question accuracy
 *   15% Learning consistency (streak, capped at 14d)
 *   10% SRS health (overdue ratio)
 */

import type { UserProfile, Question } from '../../shared/types';
import { Difficulty } from '../../shared/types';
import { ApiClient } from './apiClient';
import { CHAPTERS } from '../constants';
import { getEffectiveStreak } from './streak';

export interface ReadinessResult {
  score: number; // 0-100
  level: 'beginner' | 'intermediate' | 'almost' | 'ready';
  components: {
    simulation: number; // 0-100
    coverage: number; // 0-100
    hardAccuracy: number; // 0-100
    consistency: number; // 0-100
    srsHealth: number; // 0-100
  };
  weakestChapters: { id: number; progress: number }[];
  recommendation: string; // i18n key
}

export const READINESS_LEVELS = {
  beginner: { min: 0, color: '#ef4444', emoji: '🌱', bgClass: 'from-red-500 to-red-600' },
  intermediate: { min: 30, color: '#f59e0b', emoji: '📚', bgClass: 'from-amber-500 to-orange-500' },
  almost: { min: 60, color: '#84cc16', emoji: '🎯', bgClass: 'from-lime-500 to-green-500' },
  ready: { min: 85, color: '#22c55e', emoji: '✅', bgClass: 'from-green-500 to-emerald-500' },
} as const;

export function calculateReadiness(user: UserProfile): ReadinessResult {
  // ── 1. Simulation Score (30%) ──
  const recentSims = (user.simulationHistory || []).slice(-3);
  const simulation =
    recentSims.length > 0
      ? recentSims.reduce((sum, sim) => sum + Math.max(0, 100 - sim.errorPoints * 5), 0) /
        recentSims.length
      : 0;

  // ── 2. Chapter Coverage (25%) ──
  // Live from correctlyAnswered, same formula as the chapter rings in
  // LearnView (audit F-02: the stored chapterProgress field is never
  // written during normal learning — only the placement test sets it).
  let allQuestions: Question[] = [];
  try {
    allQuestions = ApiClient.getAllQuestions();
  } catch {
    // ApiClient may not be ready yet
  }
  const correctSet = new Set(user.correctlyAnswered || []);
  const progress: Record<number, number> = {};
  if (allQuestions.length > 0) {
    const totals = new Map<number, { total: number; correct: number }>();
    for (const q of allQuestions) {
      const entry = totals.get(q.chapterId) || { total: 0, correct: 0 };
      entry.total++;
      if (correctSet.has(q.id)) entry.correct++;
      totals.set(q.chapterId, entry);
    }
    for (const c of CHAPTERS) {
      const entry = totals.get(c.id);
      progress[c.id] = entry && entry.total > 0 ? (entry.correct / entry.total) * 100 : 0;
    }
  } else {
    // Question cache not loaded — fall back to the stored field rather
    // than reporting 0 coverage.
    Object.assign(progress, user.chapterProgress || {});
  }
  const coverage =
    CHAPTERS.length > 0
      ? CHAPTERS.reduce((sum, c) => sum + (progress[c.id] || 0), 0) / CHAPTERS.length
      : 0;

  // ── 3. Hard Question Accuracy (20%) ──
  const hardQuestions = allQuestions.filter((q) => q.difficulty === Difficulty.HARD);
  const hardCorrect = hardQuestions.filter((q) => correctSet.has(q.id));
  const hardAccuracy =
    hardQuestions.length > 0 ? (hardCorrect.length / hardQuestions.length) * 100 : 0;

  // ── 4. Learning Consistency (15%) ──
  // Effective streak: a streak whose last counted day is older than
  // yesterday is dead and must not score consistency points (F-03).
  const consistency = Math.min(100, (getEffectiveStreak(user) / 14) * 100);

  // ── 5. SRS Health (10%) ──
  // Nur SRS-Eintraege zaehlen, deren Frage noch im Katalog existiert —
  // geloeschte Fragen blieben sonst fuer immer "overdue" (Audit 2026-08,
  // Vorbedingung fuer den Duplikat-Abbau P2-1).
  const knownIds = allQuestions.length > 0 ? new Set(allQuestions.map((q) => q.id)) : null;
  const srsEntries = Object.entries(user.srsData || {}).filter(
    ([id]) => knownIds === null || knownIds.has(id)
  );
  let srsHealth: number;
  if (srsEntries.length === 0) {
    srsHealth = 50; // Neutral when no SRS data
  } else {
    const now = Date.now();
    const overdue = srsEntries.filter(([_, d]) => d.nextReview <= now).length;
    srsHealth = Math.round((1 - overdue / srsEntries.length) * 100);
  }

  // ── Weighted Sum ──
  const score = Math.round(
    0.3 * simulation + 0.25 * coverage + 0.2 * hardAccuracy + 0.15 * consistency + 0.1 * srsHealth
  );

  // ── Level Classification ──
  const level =
    score >= 85 ? 'ready' : score >= 60 ? 'almost' : score >= 30 ? 'intermediate' : 'beginner';

  // ── Weakest Chapters (top 3) ──
  const weakestChapters = [...CHAPTERS]
    .map((c) => ({ id: c.id, progress: progress[c.id] || 0 }))
    .sort((a, b) => a.progress - b.progress)
    .slice(0, 3);

  // ── Recommendation Key ──
  const recommendation =
    recentSims.length === 0
      ? 'readiness_no_simulation'
      : level === 'ready'
        ? 'readiness_ready'
        : level === 'almost'
          ? 'readiness_almost'
          : level === 'intermediate'
            ? 'readiness_intermediate'
            : 'readiness_beginner';

  return {
    score: Math.max(0, Math.min(100, score)),
    level,
    components: {
      simulation: Math.round(simulation),
      coverage: Math.round(coverage),
      hardAccuracy: Math.round(hardAccuracy),
      consistency: Math.round(consistency),
      srsHealth: Math.round(srsHealth),
    },
    weakestChapters,
    recommendation,
  };
}
